import React, { Component } from 'react'
import { withTheme } from 'styled-components';
import Proptypes from 'prop-types'
import { debounce, clamp } from 'lodash'
import Breakout from '../breakout/Breakout'
import ClickAnywhere from '../../shared/utilities/dom/ClickAnywhere'
import {
  StyledOuterDiv,
  StyledMain,
  StyledHeader,
  StyledLabel,
  StyledScrollbarHider
} from './MultiSelectTree.styles'
import TextInput from './text-input/TextInput.container'
import TreeView from './tree-view/TreeView.container'
import CaretButton from './caret-button/CaretButton.container'
import { CELL_HEIGHT } from './config'
import {
  OPENING,
  OPENED,
  CLOSING,
  CLOSED
} from './constants'

import {
  walk,
  getNodeByPath
} from './util/treeUtils'
import {
  createShadowTree
} from './util/shadowTreeUtils'
import { UNCHECKED } from './checkbox/Checkbox'

/**
 * MultiSelectTree
 *
 */
class MultiSelectTree extends Component {
  constructor(props) {
    super(props)

    this.BOXSHADOW_OPENED = '5px 5px 4px rgba(0,0,0,0.2)'
    this.BOXSHADOW_CLOSED = '0 0 0 rgba(0,0,0,0)'

    this.state = {
      isOpen: false,
      status: CLOSED,
      hasScrollbar: false,
      height: CELL_HEIGHT,
      label: props.label,
      numNodes: 0,
      numSelected: 0,
      searchTerm: '',
      showSearch: false,
      isTickAfterOpen: false
    }

    this.onSearch = debounce(this.onSearch, 300)

    this.timeouts = {}
    this.mainEl = null
    this.outerEl = null
    this.numNodesVisibleMax = 7 // If more items than this scrolling is active
    this.clickAnywhere = new ClickAnywhere()

    this.createShadowTree(props.data)
    
  }

  /**
   * The shadowTree is created here, but passed into and managed entirely by TreeView.
   * That's because with <Breakout> TreeView is inevitably re-mounted each time it's opened,
   * all local state is wiped. By keeping the instance here we can maintain its state.
   *
   * @param {Array} data - The tree
   */
  createShadowTree(data) {
    const acronymize = title => title.split(' ').map(s => s.substring(0, 1).toUpperCase()).join('')

    this.shadowTree = createShadowTree(data)

    // Augments shadowTree with data and state
    walk(this.shadowTree, node => {
      // Data title and value
      const dataNode = getNodeByPath(data, node.path)

      node.title = dataNode.title

      // Values (leafs only) - our current test data samples don't have values, so create pseudo values
      // by using acronyms of titles plus id, example: 'Botswana Stock Exchange' -> 'BSE-3'
      if (!node.isParent) {
        node.value = (dataNode.value !== undefined) ? dataNode.value : `${acronymize(dataNode.title)}-${node.id}`
      }

      // Checked/search/display state
      node.checkboxStatus = UNCHECKED
      node.searchTermIndex = -1
      if (node.isParent) {
        node.numSelected = 0
      }

      // Key for React to ensure consistency, i.e. not recreating components
      node.key = `n${node.id}`

      // Initial state: show top levels closed
      node.isShown = (node.path.length < 3)
      if (node.isParent) node.isOpen = (node.path.length < 2)
    })
  }

  onRefMain = el => {
    if (el && el.attrs) return // Styled-components v.3/v.4 inconsistency ref/innerRef
    this.mainEl = el
  }

  onRefOuter = el => {
    if (el && el.attrs) return // Styled-components v.3/v.4 inconsistency ref/innerRef
    this.outerEl = el
  }

  onChange = values => {
    this.setState({ numSelected: values.length })
    this.props.onChange(values)
  }

  componentWillUnmount() {
    for(const t in this.timeouts) clearTimeout(this.timeouts[t])
    this.clickAnywhere.stop()
  }

  componentDidUpdate(prevProps, prevState) {
    const didOpen = this.state.isOpen && (prevState.isOpen !== this.state.isOpen)
    const didClose = !this.state.isOpen && (prevState.isOpen !== this.state.isOpen)
    const isTickAfterOpen = this.state.isTickAfterOpen && ( prevState.isTickAfterOpen !== this.state.isTickAfterOpen )

    if (didOpen || didClose) {
      clearTimeout(this.timeouts.showSearch)
      clearTimeout(this.timeouts.afterOpen)
    }

    if (didOpen) {
      // Timeout to allow transition
      this.timeouts.afterOpen = setTimeout(() => this.setState({ isTickAfterOpen: true }), 0)
      this.timeouts.showSearch = setTimeout(() => this.setState({ showSearch: true }), 200)
    }
    
    if (isTickAfterOpen){ 
      // Wait a tick for delayed refs due to <Breakout>
      this.clickAnywhere.except([ this.mainEl ]).toCall( this.toggleOpen )
    }
    else if (didClose){
      this.clickAnywhere.stop()
    }

  }

  shouldComponentUpdate(nextProps, nextState) {
    const should = (nextState.isOpen !== this.state.isOpen ||
      nextState.searchTerm !== this.state.searchTerm ||
      nextState.showSearch !== this.state.showSearch ||
      nextState.height !== this.state.height ||
      nextState.status !== this.state.status ||
      nextState.hasScrollbar !== this.state.hasScrollbar ||
      nextState.label !== this.state.label ||
      nextState.isTickAfterOpen !== this.state.isTickAfterOpen ||
      nextProps.values !== this.props.values)

    return should
  }

  static getDerivedStateFromProps(props, state) {
    return {
      numSelected: props.values ? props.values.length : 0,
      searchTerm: (props.values === null) ? '' : state.searchTerm
    }
  }

  toggleOpen = () => {
    if (!this.state.isOpen) {
      this.setState({ 
        isOpen: true, 
        status: OPENING, 
        hasScrollbar: false,
        searchTerm: ''
      })
    } else {
      this.setState({
        isOpen: false,
        status: CLOSING,
        hasScrollbar: false,
        showSearch: false,
        height: CELL_HEIGHT,
        isTickAfterOpen: false
      })
    }
  }

  onSearch = e => {
    this.setState({ searchTerm: e.target.value })
  }

  onTreeChangeHeight = numNodes => {
    const height = this.calculateHeight(numNodes)
    const hasScrollbar = this.shouldScroll(numNodes)

    this.setState({ height, hasScrollbar, numNodes })
  }

  calculateHeight = numItems => {
    // @TODO this.numNodesVisibleMax to be adjusted later, based on available space
    const treeContentHeight = numItems * CELL_HEIGHT
    const treeHeightMax = this.numNodesVisibleMax * CELL_HEIGHT
    const treeHeight = clamp(treeContentHeight, CELL_HEIGHT, treeHeightMax)
    const extraHeight = 2 // Visual ballancing

    return this.state.isOpen ? treeHeight + CELL_HEIGHT + extraHeight : CELL_HEIGHT
  }

  onToggleOpenComplete = (e) => {
    if (e.target === e.currentTarget && e.propertyName === 'height') {
      this.setState({
        status: this.state.isOpen ? OPENED : CLOSED,
        hasScrollbar: this.shouldScroll(this.state.numNodes)
      })
    }
  }

  shouldScroll(numNodes) {
    return (this.state.status === OPENED || this.state.status === OPENING)
      ? (numNodes > this.numNodesVisibleMax)
      : false
  }

  render() {
    const { isOpen, height, status, hasScrollbar, numSelected, isTickAfterOpen } = this.state

    const useBreakout = window.innerWidth > this.props.theme.responsive.breakpoints.md
    const isBreakoutActive = useBreakout && status !== CLOSED

    const labelText = (numSelected === 0)
      ? this.props.label
      : <React.Fragment>{this.props.label}<strong> ({numSelected})</strong></React.Fragment>

    const main = (
      <StyledMain
        data-name='MAIN'
        isOpen={isOpen}
        height={height}
        isTickAfterOpen={isTickAfterOpen}
        ref={this.onRefMain}
        innerRef={this.onRefMain}
        onTransitionEnd={this.onToggleOpenComplete}
      >
        <StyledHeader data-name='HEADER'>
          <TextInput visible={this.state.showSearch} value={this.state.searchTerm} placeholder={this.props.searchPlaceholder} onChange={this.onSearch} tabIndex='2' />
          <StyledLabel visible={!this.state.showSearch} onClick={this.toggleOpen}>{labelText}</StyledLabel>
        </StyledHeader>
        <TreeView
          shadowTree={this.shadowTree}
          onChange={this.onChange}
          searchTerm={this.state.searchTerm}
          searchNoResults={this.props.searchNoResults}
          onChangeHeight={this.onTreeChangeHeight}
          isOpen={isOpen}
          status={status}
          hasScrollbar={hasScrollbar}
          numNodesVisibleMax={this.numNodesVisibleMax}
          values={this.props.values}
        />
        <StyledScrollbarHider isVisible={!hasScrollbar} />
        <CaretButton angle={isOpen ? 0 : -180} onClick={this.toggleOpen} tabIndex='1' />
      </StyledMain>
    )

    return (
      <StyledOuterDiv data-name='OUTER' ref={this.onRefOuter} innerRef={this.onRefOuter} useBreakout={useBreakout}>
        {
          isBreakoutActive
            ? <Breakout trackElement={this.outerEl}>{ main }</Breakout>
            : main
        }
      </StyledOuterDiv>
    )
  }
}

MultiSelectTree.propTypes = {
  data: Proptypes.array.isRequired,
  onChange: Proptypes.func,
  label: Proptypes.string,
  searchPlaceholder: Proptypes.string,
  searchNoResults: Proptypes.string,
  values: Proptypes.array
}
MultiSelectTree.defaultProps = {
  label: 'Select',
  searchPlaceholder: 'Search...',
  searchNoResults: 'No results'
}

export default withTheme(MultiSelectTree)
